import React, {FC} from 'react';
import {Platform} from 'react-native';
import {PaymentsBootstrap, PaymentsProvider} from '@lookiero/payments-front';
import {PromoCodesProvider} from '@modules/promoCodes/providers/PromoCodesProvider';
import {EventProvider} from '@lookiero/event';
import {MessagesProvider} from '@i18n';
import {BASENAME} from '../../modules/routing/routes.definition';
import {FeatureFlagsProvider, SettingsProvider, UserOverallInfoProvider} from '@components/_context';
import {ModalManagerProvider} from '@components/_context/ModalManager';
import {HeadManager} from '@components/templates/HeadManager';
import {ErrorHandler, ErrorHandlerProvider, errorParser, ErrorReporter, IGNORED_ERRORS} from '../../observability';
import {AppProvider} from './App.context';
import {GoogleSignInProvider} from '@components/_context/GoogleSignInContext';
import {ConfigurationApplication} from '@application/Config';
import {Routing} from '../../modules/routing/components/Routing';
import {Router} from '../../modules/routing/router/Router';
import {WebviewProvider} from './components/Webview/Webview.context';
import {Aurora} from './App.Aurora';
import {AppLinking} from './App.Linking';
import {GestureHandlerRootView} from 'react-native-gesture-handler';
import {KeyboardProvider} from 'react-native-keyboard-controller';
import {FirebasePerformanceWrapper} from './components/Monitoring/FirebasePerformanceWrapper';
import {SafeAreaProvider} from 'react-native-safe-area-context';
import {AppBootstrap} from './App.Bootstrap';
import {StyleProfileNextBestActionHost} from '../../modules/styleProfile/ui/views/nextBestAction/StyleProfileNextBestActionHost';
import {getBuildNumber, getVersion} from 'react-native-device-info';
import {LookInProvider} from '@modules/look-in/ui/hooks/useLookIn';
import {MendelProvider} from '@modules/ab-testing/mendel';
import {PortalProvider} from '@gorhom/portal';
import './globals';

const IS_WEB = Platform.OS === 'web';

const RELEASE = `${process.env.EXPO_PUBLIC_RELEASE || 'no-release'}-${process.env.EXPO_PUBLIC_CONFIG_MODE || 'other'}`;

const App: FC = () => {
  const appVersion = getVersion();
  const appPrivateVersion = getBuildNumber();

  return (
    <Router basename={IS_WEB ? BASENAME : undefined}>
      <FirebasePerformanceWrapper>
        <ErrorHandlerProvider
          dsn={
            IS_WEB
              ? 'https://b880b7233a004016981a8a1553ad3140@o179049.ingest.sentry.io/1454233'
              : 'https://d34ee7cb457b451aaeed7a5ca7249e43@o179049.ingest.sentry.io/5552335'
          }
          ignoredErrors={IGNORED_ERRORS}
          environment={ConfigurationApplication.retrieve().environment}
          release={IS_WEB ? RELEASE : `${appVersion}-${Platform.OS}`}
          dist={appPrivateVersion}
          beforeSend={errorParser}
        >
          <ErrorHandler section="app">
            <AppLinking />
            <FeatureFlagsProvider>
              <MessagesProvider>
                <SettingsProvider>
                  <PaymentsProvider>
                    <EventProvider>
                      <AppProvider>
                        <MendelProvider>
                          <LookInProvider>
                            <WebviewProvider>
                              <ModalManagerProvider>
                                <PromoCodesProvider>
                                  <UserOverallInfoProvider>
                                    <SafeAreaProvider>
                                      <KeyboardProvider>
                                        <GestureHandlerRootView style={{flex: 1}}>
                                          <PortalProvider>
                                            <StyleProfileNextBestActionHost>
                                              <Aurora>
                                                <PaymentsBootstrap />
                                                <HeadManager />
                                                <ErrorReporter />
                                                <GoogleSignInProvider>
                                                  <Routing />
                                                </GoogleSignInProvider>

                                                <AppBootstrap />
                                              </Aurora>
                                            </StyleProfileNextBestActionHost>
                                          </PortalProvider>
                                        </GestureHandlerRootView>
                                      </KeyboardProvider>
                                    </SafeAreaProvider>
                                  </UserOverallInfoProvider>
                                </PromoCodesProvider>
                              </ModalManagerProvider>
                            </WebviewProvider>
                          </LookInProvider>
                        </MendelProvider>
                      </AppProvider>
                    </EventProvider>
                  </PaymentsProvider>
                </SettingsProvider>
              </MessagesProvider>
            </FeatureFlagsProvider>
          </ErrorHandler>
        </ErrorHandlerProvider>
      </FirebasePerformanceWrapper>
    </Router>
  );
};

export {App};
